/**
 * Per-field review controls for staff during onboarding KYC review.
 *
 * Each row shows the captured value + ✓ Approve / ✗ Reject buttons.
 * Rejection opens an inline textarea for the reason. The backend
 * persists each decision immediately on click (no batch save) — the
 * row's status badge updates live so the agent always sees the
 * source of truth. File fields show a "View" button that hands the
 * document up to the parent for the viewer pane.
 *
 * The "Send back to client" CTA is owned by the parent (CaseDetail)
 * because gating it requires knowing the global state: every field
 * decided + at least one rejected. This component just emits row-
 * level changes via `onReviewChanged` so the parent can recompute.
 */
import { useState } from 'react';

import workflowService from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import type { CaseDetailFull } from '../../types/workflow';
import { formatFieldValue } from '../../lib/workflowForm';
import { humanize } from '../../lib/stringCase';
import DocumentExpiryCapture from '../workflow/DocumentExpiryCapture';
import { needsDocumentView } from './fieldReview.helpers';


// File pointer shape stored in case_data for file fields. Same as
// the one in CaseDetail's CapturedDataSection — kept inline here so
// this component is self-contained for the review surface.
interface FilePointer {
  document_id: string;
  storage_path?: string;
  mime_type?: string;
  size_bytes?: number;
  file_hash?: string;
}

function isFilePointer(value: unknown): value is FilePointer {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as FilePointer).document_id === 'string'
  );
}

export interface ReviewableFile {
  /** field_key the document belongs to. */
  fieldKey: string;
  /** field.label (human-readable) for the viewer header. */
  label: string;
  /** UUID of the case_documents row. */
  documentId: string;
  mimeType?: string;
}

interface Props {
  caseId: string;
  detail: CaseDetailFull;
  /** Called after a successful per-field review so the parent can
   *  refetch and recompute the gate for the "Send back" button. */
  onReviewChanged: () => void;
  /** Fired when the agent clicks "View" on a file field. Parent
   *  opens the viewer pane on the right. */
  onViewDocument: (file: ReviewableFile) => void;
  /** Currently-selected document in the viewer (highlighted row). */
  selectedDocumentId: string | null;
  /** When true, every interactive control (Approve / Reject / reason
   *  textarea / Save) is disabled and visually muted. Used when the
   *  current role lacks `can_edit` on the case's current state — e.g.,
   *  the agent viewing a case in `pending_decision` after they fired
   *  SUBMIT_FOR_DECISION (one-way escalation, see kyc-review-actions-
   *  model memory). The `[View]` button on file fields stays active
   *  because viewing isn't a state mutation. */
  readOnly?: boolean;
}

const REVIEW_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:  { bg: colors.canvas,    fg: colors.muted,   label: 'Pending'  },
  approved: { bg: colors.successBg, fg: colors.success, label: 'Approved' },
  rejected: { bg: colors.dangerBg,  fg: colors.danger,  label: 'Rejected' },
};

const REJECTION_MIN = 5;
const REJECTION_MAX = 500;
/** Field keys pulled to the top of the review list (in this order) so the agent
 *  validates the captured data against the official ID first. */
const ID_REVIEW_LEAD = ['id_front'];

interface RowDerivedState {
  status: string;
  badge: { bg: string; fg: string; label: string };
  isFile: boolean;
  isBeingRejected: boolean;
  isSubmitting: boolean;
  docId: string | undefined;
  isSelectedDoc: boolean;
  needsView: boolean;
  approveDisabled: boolean;
  rejectDisabled: boolean;
  rejectCursor: 'default' | 'wait' | 'pointer';
}

interface RowStateInput {
  value: unknown;
  review: CaseDetailFull['field_reviews'][string] | undefined;
  selectedDocumentId: string | null;
  viewedDocIds: ReadonlySet<string>;
  rejectingKey: string | null;
  submittingKey: string | null;
  readOnly: boolean;
  key: string;
}

/**
 * Compute every per-row flag the review controls need in one place so
 * the render callback stays flat (keeps S3776 cognitive complexity low).
 */
function deriveRowState(input: RowStateInput): RowDerivedState {
  const { value, review, selectedDocumentId, viewedDocIds } = input;
  const { rejectingKey, submittingKey, readOnly, key } = input;
  const status = review?.review_status ?? 'pending';
  const isFile = isFilePointer(value);
  const isSubmitting = submittingKey === key;
  const docId = isFile ? value.document_id : undefined;
  const needsView = needsDocumentView({ isFile, documentId: docId, viewedDocIds });

  // Reject-button cursor: split out of a nested ternary so SonarLint
  // stays quiet (S3358) and readOnly unambiguously overrides in-flight.
  let rejectCursor: 'default' | 'wait' | 'pointer' = 'pointer';
  if (readOnly || needsView) rejectCursor = 'default';
  else if (isSubmitting) rejectCursor = 'wait';

  return {
    status,
    badge: REVIEW_BADGE[status],
    isFile,
    isBeingRejected: rejectingKey === key,
    isSubmitting,
    docId,
    isSelectedDoc: isFile && selectedDocumentId === docId,
    needsView,
    approveDisabled: readOnly || isSubmitting || status === 'approved' || needsView,
    rejectDisabled: readOnly || isSubmitting || needsView,
    rejectCursor,
  };
}

export default function FieldReviewSection({
  caseId,
  detail,
  onReviewChanged,
  onViewDocument,
  selectedDocumentId,
  readOnly = false,
}: Readonly<Props>): JSX.Element {
  // Per-row state: which field is being rejected (showing its
  // textarea), and the in-flight reason draft.
  const [rejectingKey, setRejectingKey] = useState<string | null>(null);
  const [reasonDraft, setReasonDraft] = useState('');
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  // Document UUIDs the agent has opened in the viewer this session.
  // A file field cannot be approved/rejected until its document has
  // been viewed — the view IS the evidence-of-review (see
  // fieldReview.helpers: documented, attributable KYC review).
  const [viewedDocIds, setViewedDocIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  function markDocumentViewed(documentId: string): void {
    setViewedDocIds((prev) => {
      if (prev.has(documentId)) return prev;
      const next = new Set(prev);
      next.add(documentId);
      return next;
    });
  }

  // The ID front leads the review: the agent reads it first and validates the
  // captured name / CURP / etc. against it. Everything else keeps its original
  // order. (Object.entries is insertion order otherwise.)
  const allEntries = Object.entries(detail.data).filter(([k]) => !k.startsWith('_'));
  const leadEntries = ID_REVIEW_LEAD
    .map((k) => allEntries.find(([kk]) => kk === k))
    .filter((e): e is [string, unknown] => e !== undefined);
  const restEntries = allEntries.filter(([k]) => !ID_REVIEW_LEAD.includes(k));
  const entries = [...leadEntries, ...restEntries];
  const fieldByKey = new Map(detail.fields.map((f) => [f.field_key, f]));

  async function approveField(fieldKey: string): Promise<void> {
    setRowError(null);
    setSubmittingKey(fieldKey);
    try {
      await workflowService.reviewCaseDataField(caseId, fieldKey, {
        review_status: 'approved',
      });
      onReviewChanged();
      setRejectingKey(null);
    } catch (err) {
      setRowError(getApiErrorMessage(err, 'Could not approve.'));
    } finally {
      setSubmittingKey(null);
    }
  }

  async function rejectField(fieldKey: string): Promise<void> {
    const reason = reasonDraft.trim();
    if (reason.length < REJECTION_MIN) {
      setRowError(`Rejection reason must be at least ${REJECTION_MIN} characters.`);
      return;
    }
    if (reason.length > REJECTION_MAX) {
      setRowError(`Rejection reason cannot exceed ${REJECTION_MAX} characters.`);
      return;
    }
    setRowError(null);
    setSubmittingKey(fieldKey);
    try {
      await workflowService.reviewCaseDataField(caseId, fieldKey, {
        review_status: 'rejected',
        rejection_reason: reason,
      });
      setRejectingKey(null);
      setReasonDraft('');
      onReviewChanged();
    } catch (err) {
      setRowError(getApiErrorMessage(err, 'Could not reject.'));
    } finally {
      setSubmittingKey(null);
    }
  }

  return (
    <section
      className="rounded-2xl p-5 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-sm font-semibold mb-3" style={{ color: colors.muted }}>
        CAPTURED DATA — review per field
      </h2>

      {rowError && (
        <div
          className="rounded-lg p-3 mb-3 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {rowError}
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-sm" style={{ color: colors.mutedSoft }}>
          The member has not submitted any data yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([key, value]) => {
            const fieldDef = fieldByKey.get(key);
            const label = fieldDef?.label ?? humanize(key);
            const review = detail.field_reviews[key];
            const {
              status,
              badge,
              isFile,
              isBeingRejected,
              isSubmitting,
              isSelectedDoc,
              needsView,
              approveDisabled,
              rejectDisabled,
              rejectCursor,
            } = deriveRowState({
              value,
              review,
              selectedDocumentId,
              viewedDocIds,
              rejectingKey,
              submittingKey,
              readOnly,
              key,
            });

            return (
              <li
                key={key}
                data-testid={`field-${key}`}
                className="rounded-lg p-3"
                style={{
                  background: isSelectedDoc ? colors.canvas : colors.surface,
                  border: `1px solid ${isSelectedDoc ? colors.muted : colors.border}`,
                }}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: colors.muted }}
                      >
                        {label}
                      </span>
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                        style={{ background: badge.bg, color: badge.fg }}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="text-sm break-words" style={{ color: colors.ink }}>
                      {isFile ? (
                        <span className="font-mono text-xs" style={{ color: colors.mutedSoft }}>
                          {(value as FilePointer).mime_type ?? 'file'}
                          {(value as FilePointer).size_bytes !== undefined &&
                            ` · ${(value as FilePointer).size_bytes} B`}
                        </span>
                      ) : (
                        <span>{formatFieldValue(value, fieldDef)}</span>
                      )}
                    </div>
                    {status === 'rejected' && review?.rejection_reason && (
                      <p
                        className="text-xs mt-1"
                        style={{ color: colors.danger }}
                      >
                        → {review.rejection_reason}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isFile && (
                      <button
                        type="button"
                        onClick={() => {
                          markDocumentViewed((value as FilePointer).document_id);
                          onViewDocument({
                            fieldKey: key,
                            label,
                            documentId: (value as FilePointer).document_id,
                            mimeType: (value as FilePointer).mime_type,
                          });
                        }}
                        className="px-2.5 py-1 rounded text-xs font-medium"
                        style={{
                          // Draw the eye to View while the decision is
                          // still gated on it.
                          background: needsView ? colors.warningBg : colors.canvas,
                          color: needsView ? colors.warning : colors.inkSoft,
                          border: `1px solid ${needsView ? colors.warning : colors.border}`,
                        }}
                        title={
                          needsView
                            ? 'View the document before approving or rejecting'
                            : 'View this document'
                        }
                      >
                        View
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => approveField(key)}
                      disabled={approveDisabled}
                      className="px-2.5 py-1 rounded text-xs font-semibold"
                      style={{
                        background: status === 'approved' ? colors.success : colors.successBg,
                        color: status === 'approved' ? '#FFFFFF' : colors.success,
                        border: `1px solid ${colors.success}`,
                        opacity: readOnly || isSubmitting || needsView ? 0.5 : 1,
                        cursor:
                          readOnly || isSubmitting || status === 'approved' || needsView
                            ? 'default'
                            : 'pointer',
                      }}
                      title={approveReviewTitle(readOnly, needsView)}
                    >
                      ✓ Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRejectingKey(isBeingRejected ? null : key);
                        setReasonDraft(review?.rejection_reason ?? '');
                        setRowError(null);
                      }}
                      disabled={rejectDisabled}
                      className="px-2.5 py-1 rounded text-xs font-semibold"
                      style={{
                        background: status === 'rejected' ? colors.danger : colors.dangerBg,
                        color: status === 'rejected' ? '#FFFFFF' : colors.danger,
                        border: `1px solid ${colors.danger}`,
                        opacity: readOnly || isSubmitting || needsView ? 0.5 : 1,
                        cursor: rejectCursor,
                      }}
                      title={rejectReviewTitle(readOnly, needsView)}
                    >
                      ✗ Reject
                    </button>
                  </div>
                </div>

                {isFile && (() => {
                  // Find this row's document in caseDetail.documents to
                  // surface its captured expiry. Staff can correct what
                  // the client entered (or add it if blank).
                  const docId = (value as FilePointer).document_id;
                  const doc = detail.documents.find((d) => d.document_id === docId);
                  if (!doc) return null;
                  return (
                    <DocumentExpiryCapture
                      documentId={doc.document_id}
                      fieldKey={key}
                      initial={{
                        expires_at: doc.expires_at,
                        expiry_granularity: doc.expiry_granularity,
                      }}
                      enabled={!readOnly}
                      onSave={async (id, body) => {
                        const res = await workflowService.updateDocumentExpiry(
                          caseId, id, body,
                        );
                        onReviewChanged();
                        return {
                          expires_at: res.expires_at,
                          expiry_granularity: res.expiry_granularity,
                        };
                      }}
                    />
                  );
                })()}

                {isBeingRejected && (
                  <RejectReasonEditor
                    reasonDraft={reasonDraft}
                    onChange={setReasonDraft}
                    isSubmitting={isSubmitting}
                    onCancel={() => {
                      setRejectingKey(null);
                      setReasonDraft('');
                    }}
                    onSave={() => rejectField(key)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Inline editor for a field rejection reason (member sees it verbatim). */
function RejectReasonEditor({
  reasonDraft,
  onChange,
  isSubmitting,
  onCancel,
  onSave,
}: {
  readonly reasonDraft: string;
  readonly onChange: (v: string) => void;
  readonly isSubmitting: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}): JSX.Element {
  const saveDisabled = isSubmitting || reasonDraft.trim().length < REJECTION_MIN;
  return (
    <div className="mt-2 pl-1">
      <p className="text-xs mb-1 font-semibold" style={{ color: colors.warning }}>
        ⚠ The member sees this comment verbatim on this field.
      </p>
      <textarea
        value={reasonDraft}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        maxLength={REJECTION_MAX}
        placeholder="Why is this field rejected? The member sees this verbatim."
        className="w-full text-sm p-2 rounded"
        style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
      />
      <div className="flex justify-end gap-2 mt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-3 py-1 rounded text-xs font-medium"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saveDisabled}
          className="px-3 py-1 rounded text-xs font-semibold"
          style={{
            background: colors.danger,
            color: '#FFFFFF',
            opacity: saveDisabled ? 0.5 : 1,
            cursor: saveDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {isSubmitting ? '…' : 'Save rejection'}
        </button>
      </div>
    </div>
  );
}

/** Tooltip for the Approve button given the row's gating state. */
function approveReviewTitle(readOnly: boolean, needsView: boolean): string {
  if (readOnly) return 'Case is with the supervisor — read-only.';
  if (needsView) return 'View the document before approving.';
  return 'Approve this field';
}

/** Tooltip for the Reject button given the row's gating state. */
function rejectReviewTitle(readOnly: boolean, needsView: boolean): string {
  if (readOnly) return 'Case is with the supervisor — read-only.';
  if (needsView) return 'View the document before rejecting.';
  return 'Reject this field';
}
